import { createSignal, type Accessor } from 'solid-js'
import type { FeatureFlagKey } from './registry'

export const OVERRIDES_STORAGE_KEY = 'af-feature-overrides'

export type OverridesMap = Partial<Record<FeatureFlagKey, boolean>>

declare global {
  interface Window {
    featureToggles: {
      enable: (key: string) => void
      disable: (key: string) => void
      reset: () => void
      list: () => OverridesMap
    }
  }
}

/**
 * Parse the current query string for `?feature=<key>` overrides.
 *
 * Returns:
 *   - `null` for `?feature=reset` (clear-all sentinel)
 *   - an override map otherwise (empty object if no `feature` param)
 */
export function parseOverridesFromQueryString(search: string): OverridesMap | null {
  const params = new URLSearchParams(search)
  const feature = params.get('feature')
  if (feature === null) return {}
  if (feature === 'reset') return null
  const off = params.get('off')
  const value = off !== '1'
  return { [feature]: value } as OverridesMap
}

export function readOverridesFromStorage(): OverridesMap {
  try {
    const raw = sessionStorage.getItem(OVERRIDES_STORAGE_KEY)
    if (raw === null) return {}
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {}
    }
    return parsed as OverridesMap
  } catch {
    return {}
  }
}

function writeOverridesToStorage(overrides: OverridesMap): void {
  try {
    sessionStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(overrides))
  } catch {
    // sessionStorage can throw SecurityError (Safari private mode),
    // QuotaExceededError, or be unavailable entirely. In-memory state
    // is still correct — overrides just won't survive a reload.
  }
}

function clearOverridesStorage(): void {
  try {
    sessionStorage.removeItem(OVERRIDES_STORAGE_KEY)
  } catch {
    // See writeOverridesToStorage above for the rationale.
  }
}

/**
 * Create a reactive signal of the current overrides map.
 *
 * On creation:
 *   1. Reads the current URL for `?feature=...` overrides.
 *   2. Merges them into any existing sessionStorage state (or clears it on reset).
 *   3. Exposes `window.featureToggles` for DevTools ergonomics.
 */
export function createOverridesSignal(): readonly [Accessor<OverridesMap>, (key: string, value: boolean) => void] {
  const fromQuery = parseOverridesFromQueryString(window.location.search)
  let initial: OverridesMap

  if (fromQuery === null) {
    // reset sentinel
    clearOverridesStorage()
    initial = {}
  } else {
    initial = { ...readOverridesFromStorage(), ...fromQuery }
    if (Object.keys(fromQuery).length > 0) {
      writeOverridesToStorage(initial)
    }
  }

  const [overrides, setOverrides] = createSignal<OverridesMap>(initial)

  const setOverride = (key: string, value: boolean): void => {
    const next = { ...overrides(), [key]: value } as OverridesMap
    setOverrides(next)
    writeOverridesToStorage(next)
  }

  const reset = (): void => {
    setOverrides({})
    clearOverridesStorage()
  }

  // TODO: In production builds this should be stripped via Vite's dead-code
  // elimination of `import.meta.env.DEV` blocks. However, the Jest/Babel
  // pipeline does not transform `import.meta.env`, so the guard has been
  // removed to keep tests working. Production stripping should be revisited
  // when a babel-plugin for import.meta (e.g., babel-plugin-transform-vite-meta-env)
  // is added to the project's babel config, or the Jest transform is updated.
  window.featureToggles = {
    enable: (key) => setOverride(key, true),
    disable: (key) => setOverride(key, false),
    reset,
    list: () => overrides()
  }

  return [overrides, setOverride] as const
}
